import React, { useCallback, useMemo } from 'react';
import { difference, isEqual, uniq, truncate } from 'lodash';
import { DataGrid, nlNL, daDK, enUS } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Badge } from '../ui-kit';

import SkillIconList from '../general/SkillIconList';
import CrewTypeLabel from '../general/CrewTypeLabel';
import AvailabilityIcon from './AvailabilityIcon';
import AvailabilityCodeIcon from '../general/AvailabilityCodeIcon';
import { StationAndTeams } from '../general/GroupName';
import ContractStatisticsProgress from '../contracts/ContractStatisticsProgress';
import ContractConstraintIndicator from '../contracts/ContractConstraintIndicator';
import round from '../../utils/round';
import { List, ListItem, ListItemIcon, Stack } from '@mui/material';
import './styles.css';
import { FormattedPeriod } from '../../utils/time';
import { theme } from '../ui-kit/helpers';

// We need to import the locales from the Data Grid package
// and supply them to the data grid through a theme. We will
// selectively import the supported locales to provent overloading
const supportedLocales = {
  nl: nlNL,
  'da-DK': daDK,
  'en-GB': enUS
};

const tableStyle = { fontSize: '1.4rem' };

const Table = ({
  candidates,
  timeUnit,
  dutyRequestId,
  updateDutyRequest,
  selectedCandidates,
  visibilityStatus,
  selectedContractTypeName
}) => {
  const rowHeight = useCallback(() => 'auto', []);

  // Format the contract statistics to the structure expected by
  // the Contract Statistics components
  // It will return a datastructure with the following elements:
  //
  // - user_id: ID of the user
  // - selected: the selected contract, which is determined by the
  //   selectedContractTypeName
  // - other: an array of contracts that the user has, minus selected
  // - present: whether there is at least one contract present
  // - loaded: whether the contracts have been loaded
  const getContractStatistics = candidate => {
    const statsForTimeUnit = candidate.contracts[timeUnit] || [];

    // If the 'any' option is chosen, use the first, otherwise select one by name
    const selectedContractStats =
      selectedContractTypeName && selectedContractTypeName !== 'any'
        ? statsForTimeUnit.find(c => c.contract_type_name === selectedContractTypeName)
        : statsForTimeUnit[0];

    const otherContractStats = statsForTimeUnit.filter(c => c !== selectedContractStats);

    return {
      user_id: candidate.user.id,
      selected: selectedContractStats,
      other: otherContractStats,
      present: !!selectedContractStats,
      loaded: candidate.contracts.loaded
    };
  };

  const balance = value => {
    // value sometimes is a string, we need to parse them into numbers first
    const roundedValue = round(value, 2);

    if (value > 0) {
      return `+${roundedValue}`;
    }

    return roundedValue;
  };

  const columns = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: I18n.t('activerecord.attributes.user.name'),
        minWidth: 200
      },
      {
        field: 'skills',
        sortable: false,
        headerName: I18n.t('activerecord.models.skill'),
        minWidth: 200,
        renderCell: params => <SkillIconList skills={params.row.skills} />
      },
      {
        field: 'availability_codes',
        sortable: false,
        headerName: I18n.t('activerecord.models.schedule/availability_code.other'),
        minWidth: 150,
        renderCell: params => (
          <div>
            {params.row.availability_codes.map(code => (
              <AvailabilityCodeIcon key={code.id} code={code} />
            ))}
          </div>
        )
      },
      {
        field: 'comments',
        sortable: false,
        headerName: I18n.t('activerecord.attributes.schedule/exchanges/duty_transaction.comments'),
        minWidth: 200,
        renderCell: params => <div>{(params.row.comments || []).join('. ')}</div>
      },
      {
        field: 'crew_type',
        headerName: I18n.t('activerecord.attributes.group.crew_type'),
        minWidth: 100,
        sortable: false,
        renderCell: params => (
          <div>
            {params.row.crew_types.map((ct, index) => (
              <p key={index}>
                <CrewTypeLabel crewType={ct} displayText={`${I18n.t(`classy_enum.station_crew_type.${ct}`)}`} />
              </p>
            ))}
          </div>
        )
      },
      {
        field: 'memberOf',
        headerName: I18n.t('member_of'),
        minWidth: 240,
        sortable: false,
        renderCell: params => {
          // Rendering a list of groups would occupy a lot of vertical space. Instead
          // we use the StationWithTeams component which renders the station as header
          // and the teams as horizontal spans. The data comes from the candidates API
          // so we need to do a little bit of logic to connect stations with teams
          const stations = params.row.groups.filter(g => g.type === 'station');

          return (
            <List dense>
              {stations.map(station => {
                const teams = params.row.groups.filter(g => g.type === 'team' && g.parent_group_id === station.id);

                return (
                  <ListItem key={station.id}>
                    <ListItemIcon sx={{ minWidth: '20px' }}>
                      <AvailabilityIcon candidate={params.row} group={station} />
                    </ListItemIcon>

                    <StationAndTeams
                      station={station}
                      teams={teams}
                      customStyles={{ maxWidth: '200px', overflowX: 'clip' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          );
        }
      },
      {
        field: 'candidate_type',
        headerName: I18n.t('schedules.exchanges.duty_exchange.candidates.candidate_type'),
        minWidth: 200,
        renderCell: params => (
          <Stack>
            <p>{I18n.t(`classy_enum.duty_transaction_candidate_type.${params.row.candidate_type}`)}</p>
            {params.row.candidate_type !== 'fully_available' &&
              params.row.can_take_over_intervals.map(interval => (
                <p key={`${interval.start_time}+${interval.end_time}`}>
                  <FormattedPeriod start={interval.start_time} end={interval.end_time} />
                </p>
              ))}
          </Stack>
        )
      },
      {
        field: 'balance',
        headerName: I18n.t('activerecord.attributes.reporting/contracts/contract.balance'),
        width: 250,
        valueGetter: params => params.row.stats.selected?.balance,
        renderCell: params => {
          if (!params.row.stats.selected) return;

          // if some contracts for this user were loaded, but no stats are
          // present, show a loader
          if (params.row.stats.selected?.loaded && !params.row.stats.selected.present)
            return <p>{I18n.t('no_results_found')}</p>;

          const userStats = [params.row.stats.selected, ...params.row.stats.other];

          return (
            <List dense>
              {userStats.map(stats => (
                <React.Fragment key={stats.id}>
                  <ListItem>
                    <div style={{ width: '100%' }}>
                      <Badge style={{ maxWidth: '160px' }} variant="secondary" title={stats.contract_type_name}>
                        {truncate(stats.contract_type_name, { length: 24 })}
                      </Badge>{' '}
                      <div style={{ float: 'right' }}>{balance(stats.balance)}</div>
                    </div>
                  </ListItem>
                  <ListItem>
                    <ContractStatisticsProgress contractStatistics={stats} customClassName={'fullWidth'} />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          );
        }
      },
      {
        field: 'constraints',
        headerName: I18n.t('reports.group_reports.contract_fulfillments.constraints'),
        sortable: false,
        width: 100,
        renderCell: params => <ContractConstraintIndicator contractStatistics={params.row.stats.selected} />
      }
    ],
    []
  );

  const selectCandidates = useCallback(
    selectedIds => {
      const candidateIds = candidates.map(c => c.id);
      // We want to keep the selected values that are not rendered
      const idsToKeep = difference(selectedCandidates, candidateIds);
      // Change the local state, this will trigger a re-render
      const newIds = uniq([...idsToKeep, ...selectedIds]);
      // If there is no change, stop, otherwise we have an endless loop
      if (isEqual(newIds, selectedCandidates)) return;

      updateDutyRequest(dutyRequestId, { candidate_user_ids: newIds });
    },
    [dutyRequestId, selectedCandidates, candidates]
  );

  // Provide a theme to set up the correct locale.
  // Info here: https://mui.com/x/react-data-grid/localization/
  const customTheme = createTheme(theme, supportedLocales[I18n.locale]);

  const candidatesWithStats = useMemo(
    () =>
      candidates.map(candidate => ({
        ...candidate,
        stats: getContractStatistics(candidate)
      })),
    [candidates, timeUnit, selectedContractTypeName]
  );

  return (
    <ThemeProvider theme={customTheme}>
      <DataGrid
        className="candidatesTable"
        sx={tableStyle}
        getRowHeight={rowHeight}
        checkboxSelection={true}
        rows={candidatesWithStats}
        columns={columns}
        disableColumnMenu={true}
        columnVisibilityModel={visibilityStatus}
        selectionModel={selectedCandidates}
        onSelectionModelChange={selectCandidates}
      />
    </ThemeProvider>
  );
};

export default Table;
